import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/config/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { resolveActiveBrandKitId } from '@/lib/identity/resolveActiveBrandKitId';

/**
 * Mora Reach is a workspace-gated module, not a global feature. The sidebar
 * row (UX PRD §7.3) must be hidden by default and shown only to workspaces
 * with the module enabled — never a greyed/disabled row.
 *
 * Internal Reach workspaces: ITC Tax and Mora AI Studio.
 */
export const ITC_WORKSPACE_ID = '6b0284d4-eabd-42cc-a349-f68a175be338';

export const REACH_ENABLED_WORKSPACE_IDS = [
  ITC_WORKSPACE_ID, // ITC Tax
  'dc05fdb2-04ee-4fb9-9cea-b3291c157097', // Mora AI Studio
] as const;

/**
 * Resolves whether Reach is enabled for a given workspace (brand kit) id.
 *
 * Dev is always entitled — `import.meta.env.DEV` — so the founder and QA can
 * exercise the route locally on the test account. Reads `import.meta.env`
 * through the same guarded pattern as `src/lib/feature-flags.ts` (a direct
 * property read wrapped in try/catch), because this module is also
 * exercised outside the browser (vitest / SSR-shaped runners) where
 * `import.meta.env` is not guaranteed to be a plain, safely-accessible
 * object — an unguarded read there has thrown in this codebase before.
 */
function isDevRuntime(): boolean {
  try {
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

export function isReachEnabledForWorkspace(workspaceId: string | null | undefined): boolean {
  if (isDevRuntime()) return true;
  if (!workspaceId) return false;
  return (REACH_ENABLED_WORKSPACE_IDS as readonly string[]).includes(workspaceId);
}

export interface ReachEntitlement {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Resolves the active workspace and returns whether Reach is enabled for it.
 *
 * `enabled` is never `true` while still loading (auth loading or the
 * workspace lookup still in flight) — matching the Integrations-Loading-Flash
 * rule in the top-level CLAUDE.md: couple `isLoading` with auth loading, not
 * just the async lookup's own pending state.
 */
export function useReachEntitlement(): ReachEntitlement {
  const { user, loading: authLoading } = useAuth();
  // Its own key, never `activePointer`: that key caches the RAW
  // user_profiles pointer (null for a teammate who never switched), while this
  // query caches the RESOLVED workspace. Sharing it let whichever query ran
  // first answer for both — an editor granted only ITC Tax saw "Reach isn't
  // enabled" because the gate read their null pointer (2026-09-29).
  const workspaceQuery = useQuery({
    queryKey: queryKeys.brandKits.effectiveWorkspace(user?.id),
    queryFn: () => resolveActiveBrandKitId(),
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });
  const workspaceId = workspaceQuery.data ?? null;

  // A disabled query without cached data reports `isLoading`, but an
  // unauthenticated user has no workspace lookup in flight. Keep the former
  // gate semantics: loading means auth is resolving or an authenticated
  // user's active-workspace lookup is resolving.
  const isLoading = authLoading || (Boolean(user?.id) && workspaceQuery.isLoading);

  return {
    enabled: !isLoading && isReachEnabledForWorkspace(workspaceId),
    isLoading,
  };
}
